/**
 * Spreadsheet grid.
 *
 * Virtualised in both axes. A workbook with 200 000 rows is ordinary, and the
 * DOM cannot hold that many elements — so only the cells inside the viewport
 * (plus a small overscan) exist at any moment, positioned absolutely inside a
 * spacer of the full scroll size (§33).
 *
 * Row and column headers are sticky rather than duplicated, which keeps the
 * scroll position and the header in exact agreement without a second scroll
 * container to synchronise.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatRef, getCell, parseRef } from '@moduviewer/handlers';
import type { XlsxSheet } from '@moduviewer/handlers';

const ROW_HEIGHT = 26;
const COL_WIDTH = 116;
const HEAD_WIDTH = 52;
const HEAD_HEIGHT = 26;
const OVERSCAN = 6;

export interface SheetViewerProps {
  readonly sheet: XlsxSheet;
  readonly selected: string | null;
  readonly onSelect: (ref: string) => void;
  /**
   * Editing, when the session allows it.
   *
   * Absent means the grid is a grid: no double-click handler, no input, no
   * chance of a stray keystroke changing a number in a document someone is
   * only reading (§4.7).
   */
  readonly editing?: {
    readonly onCommit: (ref: string, value: string) => void;
  } | undefined;
}

export function SheetViewer({
  sheet, selected, onSelect, editing,
}: SheetViewerProps): React.JSX.Element {
  const scroller = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ top: 0, left: 0, height: 600, width: 900 });
  /** The cell being typed into, if any. */
  const [editingRef, setEditingRef] = useState<string | null>(null);

  // A little headroom past the last populated cell, so a sheet does not feel
  // like it ends exactly where the data does.
  const rows = Math.max(sheet.rowCount + 8, 24);
  const cols = Math.max(sheet.colCount + 4, 10);

  const byRef = useMemo(() => {
    const map = new Map<string, (typeof sheet.cells)[number]>();
    for (const cell of sheet.cells) map.set(cell.ref, cell);
    return map;
  }, [sheet]);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setView({
      top: el.scrollTop, left: el.scrollLeft,
      height: el.clientHeight, width: el.clientWidth,
    });
  }, []);

  const firstRow = Math.max(0, Math.floor(view.top / ROW_HEIGHT) - OVERSCAN);
  const lastRow = Math.min(rows, Math.ceil((view.top + view.height) / ROW_HEIGHT) + OVERSCAN);
  const firstCol = Math.max(0, Math.floor((view.left - HEAD_WIDTH) / COL_WIDTH) - OVERSCAN);
  const lastCol = Math.min(cols, Math.ceil((view.left + view.width) / COL_WIDTH) + OVERSCAN);

  const cells: React.JSX.Element[] = [];
  for (let r = firstRow; r < lastRow; r++) {
    for (let c = firstCol; c < lastCol; c++) {
      const ref = formatRef(r, c);
      const cell = byRef.get(ref);
      const isSelected = ref === selected;
      if (!cell && !isSelected) continue;
      if (editing && ref === editingRef) {
        cells.push(
          <CellEditor
            key={ref}
            initial={cell?.formula ? `=${cell.formula}` : cell?.text ?? ''}
            style={{
              top: HEAD_HEIGHT + r * ROW_HEIGHT,
              left: HEAD_WIDTH + c * COL_WIDTH,
              width: COL_WIDTH,
              height: ROW_HEIGHT,
            }}
            label={ref}
            onCommit={(value) => {
              editing.onCommit(ref, value.startsWith('=') ? value.slice(1) : value);
              setEditingRef(null);
            }}
            onCancel={() => setEditingRef(null)}
          />,
        );
        continue;
      }

      cells.push(
        <div
          key={ref}
          className={`sheet__cell${cell?.numeric !== null && cell?.numeric !== undefined ? ' sheet__cell--num' : ''}${cell?.formula ? ' sheet__cell--formula' : ''}`}
          style={{
            top: HEAD_HEIGHT + r * ROW_HEIGHT,
            left: HEAD_WIDTH + c * COL_WIDTH,
            width: COL_WIDTH,
            height: ROW_HEIGHT,
          }}
          data-selected={isSelected ? 'true' : undefined}
          onClick={() => onSelect(ref)}
          onDoubleClick={editing ? () => { onSelect(ref); setEditingRef(ref); } : undefined}
          role="gridcell"
          tabIndex={-1}
          title={cell?.formula ? `= ${cell.formula}` : undefined}
        >
          {cell?.text ?? ''}
        </div>,
      );
    }
  }

  const colHeads: React.JSX.Element[] = [];
  for (let c = firstCol; c < lastCol; c++) {
    colHeads.push(
      <div
        key={`c${c}`}
        className="sheet__head sheet__head--col"
        style={{ left: HEAD_WIDTH + c * COL_WIDTH, width: COL_WIDTH, height: HEAD_HEIGHT }}
      >
        {formatRef(0, c).replace(/\d+$/, '')}
      </div>,
    );
  }

  const rowHeads: React.JSX.Element[] = [];
  for (let r = firstRow; r < lastRow; r++) {
    rowHeads.push(
      <div
        key={`r${r}`}
        className="sheet__head sheet__head--row"
        style={{ top: HEAD_HEIGHT + r * ROW_HEIGHT, width: HEAD_WIDTH, height: ROW_HEIGHT }}
      >
        {r + 1}
      </div>,
    );
  }

  const active = selected ? getCell(sheet, selected) : null;

  return (
    <>
      <div className="formula-bar">
        <span className="formula-bar__ref">{selected ?? '—'}</span>
        <span className="sep" />
        <span className="formula-bar__value truncate">
          {active?.formula ? `= ${active.formula}` : active?.text ?? ''}
        </span>
        {active?.formula ? (
          <span className="badge badge--warn" title="모두뷰어는 수식을 다시 계산하지 않습니다">
            재계산 안 함
          </span>
        ) : null}
      </div>

      <div className="canvas sheet" ref={scroller} onScroll={onScroll} role="grid"
        aria-rowcount={rows} aria-colcount={cols}
      >
        <div
          className="sheet__grid"
          style={{ width: HEAD_WIDTH + cols * COL_WIDTH, height: HEAD_HEIGHT + rows * ROW_HEIGHT }}
        >
          <div
            className="sheet__head sheet__corner"
            style={{ width: HEAD_WIDTH, height: HEAD_HEIGHT }}
            aria-hidden="true"
          />
          {colHeads}
          {rowHeads}
          {cells}
        </div>
      </div>
    </>
  );
}

/** Move the selection with the arrow keys (§26 keyboard operation). */
export function moveSelection(
  ref: string | null, key: string, rows: number, cols: number,
): string | null {
  const current = ref ? parseRef(ref) : null;
  if (!current) return formatRef(0, 0);
  let { row, col } = current;
  switch (key) {
    case 'ArrowUp': row = Math.max(0, row - 1); break;
    case 'ArrowDown': row = Math.min(rows - 1, row + 1); break;
    case 'ArrowLeft': col = Math.max(0, col - 1); break;
    case 'ArrowRight': col = Math.min(cols - 1, col + 1); break;
    case 'Home': col = 0; break;
    case 'End': col = Math.max(0, cols - 1); break;
    default: return ref;
  }
  return formatRef(row, col);
}

/**
 * In-place cell entry.
 *
 * A separate component so it can own its draft and its focus. Rendered in the
 * grid's own absolute coordinates, so it sits exactly over the cell it
 * replaces rather than beside it.
 */
function CellEditor(
  { initial, style, label, onCommit, onCancel }: {
    initial: string;
    style: React.CSSProperties;
    label: string;
    onCommit: (value: string) => void;
    onCancel: () => void;
  },
): React.JSX.Element {
  const [value, setValue] = useState(initial);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.focus();
    // Selecting the existing text means typing replaces it, which is what a
    // double-click into a filled cell means everywhere else.
    el.select();
  }, []);

  return (
    <input
      ref={input}
      className="sheet__editor"
      style={style}
      value={value}
      aria-label={`${label} 칸 편집`}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); onCommit(value); }
        if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        // Arrow keys belong to the text while typing, not to the grid.
        e.stopPropagation();
      }}
      onBlur={() => onCommit(value)}
    />
  );
}
